import { ALL_CHART_TYPES, DEFAULT_OPACITY } from "../../../constants";
import { CalculationMethod, EvaChart, TimeStep } from "../../../constants/charts.enums";
import {
  createFeatureEntities,
  isDefaultLengthNormalizationField,
  resolveExplicitNormalizationUnits,
  restoreNormalizationUnitsSentinel
} from "../../../entities/charts/eva/features";
import {
  DEFAULT_WIDTH,
  HIGHLIGHTED_WIDTH
} from "../../../entities/charts/eva/translators/constants";
import { ChartSettings } from "../../../hooks/charts";
import { DataEnum, Format, IGroupBy } from "../../../models";
import { IChartSettings } from "../../../models/chart";
import { GridWidget } from "../../../models/dashboard";
import { DashboardWidget } from "../../../models/workspace";
import { EvaChartFeatures } from "../../../types";
import { EvaChartStates } from "../../../types/factory";
import { ChartWidgetKey, MidstreamChartWidgetKey } from "../constants/widgets.constants";

function determineLockStates(
  oldSettings: IChartSettings
): Partial<EvaChartStates>["locks"] {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const determineTypeAndFormat = (value: any): { type: DataEnum; format: Format } => {
    if (value === null || value === undefined) {
      return { type: DataEnum.None, format: Format.None };
    }

    // Check if it's a date string
    const dateRegex = /^\d{4}(-\d{2}(-\d{2})?)?$/; // Matches YYYY, YYYY-MM, YYYY-MM-DD
    if (typeof value === "string" && dateRegex.test(value)) {
      const segments = value.split("-").length;
      if (segments === 1) return { type: DataEnum.Date, format: Format.Year };
      if (segments === 2) return { type: DataEnum.Date, format: Format.Month };
      if (segments === 3) return { type: DataEnum.Date, format: Format.Day };
    }

    // Check if it's a number or can be converted to one
    const num = Number(value);
    if (!isNaN(num)) {
      return {
        type: Number.isInteger(num) ? DataEnum.Integer : DataEnum.Number,
        format: Format.Number
      };
    }
  };

  const axisMinMax = oldSettings?.axisMinMax || {};

  return {
    xMin: {
      value: axisMinMax.xMin ?? undefined,
      ...determineTypeAndFormat(axisMinMax.xMin)
    },
    xMax: {
      value: axisMinMax.xMax ?? undefined,
      ...determineTypeAndFormat(axisMinMax.xMax)
    },
    yMin: {
      value: axisMinMax.yMin ?? undefined,
      ...determineTypeAndFormat(axisMinMax.yMin)
    },
    yMax: {
      value: axisMinMax.yMax ?? undefined,
      ...determineTypeAndFormat(axisMinMax.yMax)
    },
    y2Min: {
      value: axisMinMax.y2Min ?? undefined,
      ...determineTypeAndFormat(axisMinMax.y2Min)
    },
    y2Max: {
      value: axisMinMax.y2Max ?? undefined,
      ...determineTypeAndFormat(axisMinMax.y2Max)
    }
  };
}

/**
 * Translates old chart settings into new chart settings format.
 *
 * @param {IChartSettings} oldSettings - The old chart settings object to be translated.
 * @param {string} widgetType - The type of widget for which the chart settings are being translated.
 * @returns {ChartSettings} - The new chart settings object.
 */
export function translateOldChartSettingsToNewChartSettings(
  oldSettings: IChartSettings,
  widgetType: string
): ChartSettings {
  let chartType = oldSettings.chartType as EvaChart;
  // EvaChart.InjectorTotal's persisted label was "Running Total" from its introduction
  // (EVA-36, #6460, merged 2026-07-29) until it was renamed to "Injector Count Date" the
  // next day (#6472) — any dashboard saved in that window would otherwise resolve to no
  // chart type at all, since componentState.chartType is looked up by string value, not
  // by enum key.
  if ((chartType as unknown as string) === "Running Total") {
    chartType = EvaChart.InjectorTotal;
  }
  // NOTE: no alias is needed for the current-status chart's rename. This function
  // only runs for ChartWidgetKey/MidstreamChartWidgetKey widgets carrying a
  // `chartTypeId`, and a Well Status widget is neither — so an alias here could
  // never have run for it. The rename was resolved at the source instead:
  // EvaChart.WellStatusCurrent keeps its original persisted value and only the
  // display caption changed (WELL_STATUS_CURRENT_TITLE), so nothing needs
  // translating in either direction.
  const isDefaultLengthField = isDefaultLengthNormalizationField(
    oldSettings?.normalizeBy?.field
  );
  const features: Partial<EvaChartFeatures> = {
    timeStep: {
      properties: {
        value: oldSettings?.timeStep as TimeStep
      }
    },
    forecast: {
      active: oldSettings?.forecast
    },
    p10: {
      active: oldSettings?.showP10
    },
    p50: {
      active: oldSettings?.showP50
    },
    p90: {
      active: oldSettings?.showP90
    },
    average: {
      active: oldSettings?.average
    },
    screenshot: {
      active: oldSettings?.screenshot
    },
    logAxis: {
      properties: {
        x: { active: oldSettings?.log?.x },
        y: { active: oldSettings?.log?.y }
      }
    },
    chartFocus: {
      active: oldSettings?.useChartFocus,
      properties: {
        groupBy: {
          ...oldSettings?.groupBy,
          groupByField: oldSettings?.groupBy?.property
        },
        colorPalette: oldSettings?.colorPalette
      }
    },
    typewells: {
      active: oldSettings?.typewells
    },
    isProducingRateType: {
      active: oldSettings?.producing
    },
    normalization: {
      properties: {
        ...oldSettings?.normalizeBy,
        unit:
          oldSettings?.normalizeBy?.field === "Well_Design.HzLength_m"
            ? ""
            : oldSettings?.normalizeBy?.unit,
        defaultUnit:
          oldSettings?.normalizeBy?.field === "Well_Design.HzLength_m"
            ? "m"
            : oldSettings?.normalizeBy?.unit,
        useNormalizeBy: oldSettings?.useNormalizeBy
      },
      active: oldSettings?.useNormalizeBy
    },
    legend: {
      active: oldSettings?.legend?.visible
    },
    fullScreen: {
      active: oldSettings?.fullscreen
    },
    spaghetti: {
      active: oldSettings?.sum
    },
    source: {
      properties: {
        value: oldSettings?.source
      }
    },
    cutoff: {
      properties: {
        value: oldSettings?.cutoff
      }
    },
    shutInMonths: {
      properties: {
        value: oldSettings?.shutInMonths
      }
    },
    survivorBias: {
      active: oldSettings?.survivorBias
    },
    useWeightedRatioAverage: {
      active: oldSettings?.useWeightedRatioAverage
    },
    chartTypeParam: {
      properties: {
        x: oldSettings?.chartTypeParam?.x as Partial<IGroupBy>,
        y: oldSettings?.chartTypeParam?.y as Partial<IGroupBy>
      }
    },
    calculationMethod: {
      properties: {
        method: oldSettings?.stackedBarCalculation as unknown as CalculationMethod
      }
    },
    pressureChartSettings: {
      active: oldSettings?.pressureChartSettings?.productivityIndex, //productivityIndex is tied to the active property
      properties: {
        product: oldSettings?.pressureChartSettings?.product
      }
    },
    pointOpacity: {
      properties: {
        value: oldSettings?.dotOpacity
      }
    },
    pointSize: {
      properties: {
        value: oldSettings?.dotSize
      }
    },
    lineSize: {
      properties: {
        value: DEFAULT_WIDTH
      }
    },
    lineOpacity: {
      properties: {
        value: DEFAULT_OPACITY
      }
    },
    highlightedLineSize: {
      properties: {
        value: HIGHLIGHTED_WIDTH
      }
    },
    highlightedLineOpacity: {
      properties: {
        value: DEFAULT_OPACITY
      }
    },
    useLineOfBestFit: {
      active: oldSettings?.showLinesOfBestFit
    },
    showWellCount: {
      active: oldSettings?.showWellCount
    },
    histogramToggle: {
      active: oldSettings?.showHistogram
    },
    jitterPlotToggle: {
      active: oldSettings?.showScatter
    },
    unitSlope: {
      active: oldSettings?.showUnitSlope,
      properties: {
        colour: oldSettings?.unitSlopeStyle?.color,
        thickness: oldSettings?.unitSlopeStyle?.thickness,
        length: oldSettings?.unitSlopeStyle?.length,
        position: null
      }
    },
    halfSlope: {
      active: oldSettings?.showHalfSlope,
      properties: {
        colour: oldSettings?.halfSlopeStyle?.color,
        thickness: oldSettings?.halfSlopeStyle?.thickness,
        length: oldSettings?.halfSlopeStyle?.length,
        position: null
      }
    },
    quarterSlope: {
      active: oldSettings?.showQuarterSlope,
      properties: {
        colour: oldSettings?.quarterSlopeStyle?.color,
        thickness: oldSettings?.quarterSlopeStyle?.thickness,
        length: oldSettings?.quarterSlopeStyle?.length,
        position: null
      }
    },
    movingAverageDays: {
      properties: {
        value: oldSettings?.movingAverageDays
      }
    },
    annotations: {
      active: oldSettings?.showAnnotations
    },
    multiProductFocus: {
      properties: {
        products: oldSettings?.otherProducts?.filter((product: string) =>
          ["Oil", "Gas", "Water Inj", "Polymer Inj", "Water"].includes(product)
        ) as ("Oil" | "Gas" | "Water Inj" | "Polymer Inj" | "Water")[]
      }
    }
  };

  // midstream charts are saved in the db with the same name as regular well charts so we need to convert
  if (widgetType === MidstreamChartWidgetKey) {
    switch (chartType) {
      case EvaChart.RateDate:
        chartType = EvaChart.RateDateMidstream;
        break;
      case EvaChart.TotalRateDate:
        chartType = EvaChart.TotalRateDateMidstream;
        break;
      case EvaChart.CrossPlot:
        chartType = EvaChart.CrossPlotMidstream;
        break;
      case ALL_CHART_TYPES.WellContribution.label:
        chartType = EvaChart.WellContribution;
        break;
    }
  }
  const locks = determineLockStates(oldSettings);
  const translatedFeatures = createFeatureEntities(chartType, features);
  // Old-format normalizeBy never stored a `units` dictionary (the concept didn't exist yet),
  // so deepMerge inside createFeatureEntities always backfills DEFAULT_NORMALIZATION_UNITS
  // (m/ft) for whatever field is set. That's correct for the historical multi-unit default
  // field, but wrong for any other field (e.g. stage count, Peak Cal Rate) — resolve the
  // sentinel from the raw source and restore it now that createFeatureEntities has run.
  const rawNormalizeByUnits = oldSettings?.normalizeBy?.units;
  const sentinelUnits = isDefaultLengthField
    ? rawNormalizeByUnits
    : resolveExplicitNormalizationUnits(rawNormalizeByUnits);
  restoreNormalizationUnitsSentinel(translatedFeatures, sentinelUnits);
  return {
    chartType: chartType,
    product: oldSettings.product,
    features: translatedFeatures,
    locks: locks
  };
}

/**
 * Updates the `componentState` of a `GridWidget` based on the provided JSON data.
 *
 * @param {string} jsonData - The JSON string containing the data to be parsed.
 * @param {DashboardWidget} item - The dashboard widget associated with the grid layout item.
 * @param {GridWidget} gridLayoutItem - The grid layout item whose `componentState` will be updated.
 *
 * If the parsed JSON data represents old chart settings (determined by the presence of the `chartTypeId` property),
 * the function translates the old settings into the new format using `translateOldChartSettingsToNewChartSettings`.
 * Otherwise, it directly assigns the parsed data to the `componentState`.
 */
export function setGridLayoutItemComponentState(
  jsonData: string,
  item: DashboardWidget,
  gridLayoutItem: GridWidget
) {
  const parsedData = JSON.parse(jsonData);
  const isOldChartSettings = "chartTypeId" in parsedData;
  if (
    isOldChartSettings &&
    (item.type === ChartWidgetKey || item.type === MidstreamChartWidgetKey)
  ) {
    gridLayoutItem.componentState = translateOldChartSettingsToNewChartSettings(
      parsedData as IChartSettings,
      item.type
    );
  } else {
    if (parsedData?.features?.normalization?.properties) {
      const props = parsedData.features.normalization.properties;
      const isHzLength = props.field === "Well_Design.HzLength_m";
      if (!props.defaultUnit) {
        const sourceUnit = isHzLength ? "m" : "";
        props.defaultUnit = sourceUnit;
      }
      // JSON serialization drops undefined, so a unitless field loses its `units: undefined`
      // sentinel. Restore it as null so deepMerge doesn't fall back to DEFAULT_NORMALIZATION_UNITS.
      props.units = resolveExplicitNormalizationUnits(props.units);
    }

    gridLayoutItem.componentState = parsedData;
  }
}
